import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Token de invitación no válido');
      setValidatingToken(false);
      return;
    }
    setTokenValid(true);
    setValidatingToken(false);
  }, [token]);

  const validatePassword = (pass) => {
    if (pass.length < 8) return 'Mínimo 8 caracteres';
    if (!/[A-Z]/.test(pass)) return 'Debe contener al menos una mayúscula';
    if (!/[0-9]/.test(pass)) return 'Debe contener al menos un número';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validaciones
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    const passError = validatePassword(password);
    if (passError) {
      setError(passError);
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/signup`, {
        token,
        name,
        password
      });

      alert('✅ Cuenta creada exitosamente. Ahora puedes iniciar sesión.');
      navigate('/login');
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear la cuenta');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="decko-card p-8 max-w-md text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Invitación No Válida</h2>
          <p className="text-muted-foreground mb-6">
            El link de invitación no es válido o ha expirado.
          </p>
          <button onClick={() => navigate('/login')} className="btn-primary">
            Ir al Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #E6FFF5 0%, #D1FAE5 50%, #A7F3D0 100%)'
    }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-1">
          <img 
            src="/nexia-logo.png" 
            alt="Nexia" 
            className="w-[320px] mx-auto mb-1"
          />
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
            Activar Cuenta
          </h1>
          <p className="text-sm text-muted-foreground font-medium">
            Completa tu registro en Nexia
          </p>
        </div>

        {/* Form */}
        <div className="decko-card p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-foreground">
                Nombre Completo
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="input-decko w-full"
                placeholder="Juan Pérez"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-foreground">
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="input-decko w-full pr-12"
                  placeholder="Mínimo 8 caracteres"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                8 caracteres mínimo, 1 mayúscula, 1 número
              </p>
            </div>

            {/* Confirmar Password */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-foreground">
                Confirmar Contraseña
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="input-decko w-full"
                placeholder="Repetir contraseña"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Creando cuenta...' : 'Activar Cuenta'}
            </button>
          </form>
        </div>

        <p className="text-center mt-4 text-xs text-muted-foreground">
          ¿Ya tienes cuenta? <a href="/login" className="text-primary font-semibold hover:underline">Iniciar sesión</a>
        </p>
      </div>
    </div>
  );
};

export default Signup;
